import type {
  AdminContentPage,
  AdminContentPageResponse,
  AdminContentPageRevision,
  AdminContentPageRevisionListResponse,
  AdminContentPageRevisionResponse,
  ContentDocuments,
  ContentPageSlug,
} from '@mds/shared/types/content';

import { api } from '@/lib/api';

/**
 * Managed content — see `backend/docs/content.md`.
 *
 * Every write carries the ETag of the copy it was made against as `If-Match`.
 * A stale one is `CONTENT_VERSION_CONFLICT`: reload and reconcile, never retry.
 */

const base = (slug: ContentPageSlug) => `admin/content/pages/${slug}`;
const ifMatch = (etag: string) => ({ headers: { 'If-Match': etag } });

/** The current draft, its publication state, and the ETag to write against. */
export const getPage = <S extends ContentPageSlug>(slug: S): Promise<AdminContentPage<S>> =>
  api.get<AdminContentPageResponse<S>>(base(slug)).then((r) => r.data);

/** Writes a new draft revision. The public page does not change. */
export const savePage = <S extends ContentPageSlug>(
  slug: S,
  document: ContentDocuments[S],
  etag: string,
): Promise<AdminContentPage<S>> =>
  api.put<AdminContentPageResponse<S>>(base(slug), { document }, ifMatch(etag)).then((r) => r.data);

/** Makes the saved draft the live page. Unsaved edits in the form are not part of it. */
export const publishPage = <S extends ContentPageSlug>(
  slug: S,
  etag: string,
): Promise<AdminContentPage<S>> =>
  api
    .post<AdminContentPageResponse<S>>(`${base(slug)}/publish`, {}, ifMatch(etag))
    .then((r) => r.data);

/** Takes the page down. History and the draft are kept. */
export const unpublishPage = <S extends ContentPageSlug>(
  slug: S,
  etag: string,
): Promise<AdminContentPage<S>> =>
  api
    .post<AdminContentPageResponse<S>>(`${base(slug)}/unpublish`, {}, ifMatch(etag))
    .then((r) => r.data);

export const listRevisions = (
  slug: ContentPageSlug,
  query: { page?: number; limit?: number } = {},
) => api.get<AdminContentPageRevisionListResponse>(`${base(slug)}/revisions`, { query });

export const getRevision = <S extends ContentPageSlug>(
  slug: S,
  revisionNumber: number,
): Promise<AdminContentPageRevision<S>> =>
  api
    .get<AdminContentPageRevisionResponse<S>>(`${base(slug)}/revisions/${revisionNumber}`)
    .then((r) => r.data);
